import {useContext, useState} from 'react';
import {StorageObject} from '../models/StorageObject';
import {useMutation, useQueryClient} from 'react-query';
import {STORAGE_OBJECTS_QUERY_KEY} from '../init/constants';
import {APIClient} from '../backend/ApiClient';
import {LocaleContext} from '../context/LocaleContext';
import {DeleteItemDTO} from '../models/dto/DeleteItemDTO';
import {ObjectType} from '../models/ObjectType';
import {createLocale} from '../utils';

export const useDeleteItem = () => {
    const queryClient = useQueryClient();
    const apiClient = APIClient();

    const {language, country} = useContext(LocaleContext);
    const defaultLocale = createLocale(language, country);

    const [headlessDeleteAction, setHeadlessDeleteAction] = useState<boolean>(false);
    const [itemToDelete, setItemToDelete] = useState<StorageObject | null>(null);
    const [countriesToDelete, setCountriesToDelete] = useState<string[]>([]);
    const [languagesToDelete, setLanguagesToDelete] = useState<string[]>([]);
    const [localesToDelete, setLocalesToDelete] = useState<string[]>([]);
    const [isCompleteToDelete, setIsCompleteToDelete] = useState<boolean>(false);

    const initialize = () => {
        setHeadlessDeleteAction(false);
        setItemToDelete(null);
        setCountriesToDelete([]);
        setLanguagesToDelete([]);
        setLocalesToDelete([]);
        setIsCompleteToDelete(false);
    };

    const isLocaleDefault = (): boolean => {
        return (((localesToDelete !== undefined) && localesToDelete && (localesToDelete.length === 0))
            && ((countriesToDelete !== undefined) && countriesToDelete && (countriesToDelete.length === 0))
            && ((languagesToDelete !== undefined) && languagesToDelete && (languagesToDelete.length === 0))) as boolean;
    }

    const isComplete = (locales: string[]): boolean => {
        if ((isCompleteToDelete)
            || ((!headlessDeleteAction) && (locales !== undefined) && (locales.length > 0)
                && (itemToDelete && itemToDelete.labels && (itemToDelete.labels.size > 0)
                    && (Object.keys(itemToDelete.labels).filter(locale => (!locales.includes(locale))).length === 0)
                ))) {
            return true;
        }

        return false;
    };

    const deleteApiCall = (deleteItemDTO: DeleteItemDTO) => {
        if ((deleteItemDTO.item === undefined) || (!deleteItemDTO.item)) {
            return Promise.reject();
        }

        switch (deleteItemDTO.item.objectType) {
            case ObjectType.FOLDER:
                return apiClient.deleteFolder(deleteItemDTO.item.id, deleteItemDTO.countries, deleteItemDTO.languages, deleteItemDTO.locales, deleteItemDTO.isComplete);
            case ObjectType.FILE:
                return apiClient.deleteFile(deleteItemDTO.item.id, deleteItemDTO.countries, deleteItemDTO.languages, deleteItemDTO.locales, deleteItemDTO.isComplete);
            default:
                return Promise.reject();
        }
    };

    const {mutateAsync: deleteMutation, isLoading} = useMutation(deleteApiCall, {
        onSuccess: () => {
            initialize();
            queryClient.invalidateQueries(STORAGE_OBJECTS_QUERY_KEY);
        }
    });

    const deleteItem = () => {
        const deleteItemDTO: DeleteItemDTO = {
            item: itemToDelete ?? null,
            countries: countriesToDelete ?? [],
            languages: languagesToDelete ?? [],
            locales: isLocaleDefault() ? [defaultLocale] : (localesToDelete ?? [defaultLocale]),
            isComplete: isComplete(localesToDelete)
        }

        return deleteMutation(deleteItemDTO);
    };

    const cancelItem = () => {
        initialize();
    };

    return {
        headlessDeleteAction,
        setHeadlessDeleteAction,
        itemToDelete,
        setItemToDelete,
        countriesToDelete,
        setCountriesToDelete,
        languagesToDelete,
        setLanguagesToDelete,
        localesToDelete,
        setLocalesToDelete,
        isCompleteToDelete,
        setIsCompleteToDelete,
        deleteItem,
        cancelDeleteItem: cancelItem,
        isLoading
    };
};
